import { type FC, useEffect } from 'react';
import {
  Redo2,
  Undo2,
  Upload,
  X,
  // Link as LinkIcon, Crop
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { useTenantStore } from '@/hooks/data/tenant/useTenantStore';
import { NotificationDropdown } from '@/components/notifications';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useCommandContext } from '@/context/CommandContext';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { defaultPaginationSize } from '@/types/pagination';
import { useCapabilities } from '@/hooks/useCapabilities';
import { DAMNodeType, type TheNode } from '@/types/tree';
import { QuickSearch } from '@/components/quick-search';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useTreeContext } from '@/context/TreeContext';
import { Button } from '@/components/ui/button';
import { KeyboardKey } from '@/lib/keyboard';
import { EntityType } from '@/types/entity';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { useTheme } from 'next-themes';
import Image from 'next/image';

export const Navigation: FC<{
  node?: TheNode;
  singleAssetView?: boolean;
  singleAssetShare?: boolean;
  parentPath?: Array<string>;
}> = ({ node, singleAssetView, singleAssetShare, parentPath }) => {
  const { undo, redo, history, pointer } = useCommandContext();
  const { isPublicRoute, isPublicSharePage } = useCurrentPage();
  const { query, back, push, replace } = useRouter();
  const { navigateAndReset } = useDamNavigation();
  const { setUploadModalOpen, setUploadFolderPath, getActiveUploadSession } = useUploadStore();
  const { selectedNode, setReusableTreeSelectedFolder, setReusableTreeSelection } = useTreeStore();
  const { includeSubFolderAssets } = useTreeContext();
  const { setScrollToAssetId } = useBrowserStore();
  const { tenant } = useTenantStore(useShallow((s) => ({ tenant: s.tenantData?.tenant })));
  const { resolvedTheme } = useTheme();
  const { canUpload, canUndoRedo, canSearch, canViewNotifications } = useCapabilities();
  const tCommon = useTranslations('common');
  const tUpload = useTranslations('upload');

  const getPagination = useTableStore(useShallow((state) => state.getPagination));
  const findNodePath = useTreeStore(useShallow((state) => state.findNodePath));

  const { pageSize, pageIndex, sortOrder, sortValue } = getPagination(
    query?.origin === 'shares' ? EntityType.Share : EntityType.Asset,
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) {
        return;
      }
      if (e.key !== KeyboardKey.Escape) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
        return;
      }

      const t = e.target as HTMLElement | null;
      if (!t) {
        return;
      }

      // Don't hijack ESC while typing/editing
      const tag = t.tagName;
      const inFormField = t.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (inFormField) {
        return;
      }

      if (singleAssetView && !singleAssetShare) {
        void handleBack();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPublicRoute, isPublicSharePage, query.shareId, back, push, singleAssetShare, singleAssetView, parentPath]);

  // Should refactor this at somepoint, if we use url as the source of truth this complexity is not required.
  const handleBack = async () => {
    if (isPublicRoute || isPublicSharePage) {
      if (query.shareId) {
        await replace({
          pathname: `/shares/${query.shareId}`,
          query: {
            pageIndex: pageIndex === 0 ? 1 : pageIndex,
            pageSize,
            sortValue,
            sortOrder,
            queryString: query.queryString ?? '',
          },
        });
        setScrollToAssetId(String(query.assetId));
      } else {
        back();
      }
    } else {
      let pathArray;
      if (query?.origin === '' || (query?.origin && query.origin !== 'browse')) {
        pathArray = null;
      } else if (parentPath) {
        pathArray = [...parentPath];
      } else {
        const nodeId = query.folderId ?? query.albumId;
        pathArray = findNodePath(nodeId ? String(nodeId) : (node?.parentFolderId ?? ''), 'slug');
      }

      if (pathArray) {
        await replace({
          pathname: `/${query?.origin ?? 'browse'}/${pathArray.join('/')}`,
          query: {
            pageIndex: query.pageIndex ?? (pageIndex === 0 ? 1 : pageIndex),
            pageSize: query.pageSize && query.pageIndex ? query.pageSize : pageSize ? pageSize : defaultPaginationSize,
            sortValue,
            sortOrder,
            showSubFolderAssets: includeSubFolderAssets,
            queryString: query.queryString ?? '',
            ...(query.filters ? { filters: query.filters } : {}),
            ...(query.query || query.query === '' ? { query: query.query } : {}),
            ...(query.searchFilters ? { searchFilters: query.searchFilters } : {}),
          },
        });
        setScrollToAssetId(String(query.id));
      } else if (query?.origin && query.origin !== 'browse') {
        // The origin was not the browse section or was done via quick-search.
        await replace({
          pathname: `/${query?.origin}`,
          query: {
            pageIndex: query.pageIndex ?? (pageIndex === 0 ? 1 : pageIndex),
            pageSize: query.pageSize && query.pageIndex ? query.pageSize : pageSize ? pageSize : defaultPaginationSize,
            sortValue,
            sortOrder,
            queryString: query.queryString ?? '',
            ...(query.filters ? { filters: query.filters } : {}),
            ...(query.query || query.query === '' ? { query: query.query } : {}),
            ...(query.searchFilters ? { searchFilters: query.searchFilters } : {}),
          },
        });
      } else {
        // Return home if there is no clear return path.
        void navigateAndReset(`/${query?.origin}`, {
          pageIndex: query.pageIndex ?? (pageIndex === 0 ? 1 : pageIndex),
          pageSize: query.pageSize && query.pageIndex ? query.pageSize : pageSize ? pageSize : defaultPaginationSize,
          ...(query.filters ? { filters: query.filters } : {}),
          ...(query.query || query.query === '' ? { query: query.query } : {}),
          ...(query.searchFilters ? { searchFilters: query.searchFilters } : {}),
        });
      }
    }
  };

  return (
    <div className="flex h-12 w-full items-center justify-between leading-4">
      <div
        className={cn(
          'flex h-full items-center justify-between gap-x-2 bg-ui-base',
          isPublicRoute || isPublicSharePage ? 'grow' : 'w-64 min-w-64 max-w-64',
        )}
      >
        <div
          className="flex cursor-pointer items-center gap-x-2"
          onClick={() => {
            void navigateAndReset('/browse', {
              pageIndex: 1,
              pageSize: query.pageSize && query.pageIndex ? query.pageSize : defaultPaginationSize,
            });
          }}
        >
          <Image
            alt="logo"
            width={48}
            height={48}
            draggable={false}
            src={
              process.env.NEXT_PUBLIC_ENVIRONMENT === 'local'
                ? `/lkbks-${resolvedTheme}.svg`
                : (tenant?.logo ?? `${process.env.NEXT_PUBLIC_ASSET_PREFIX}/lkbks-${resolvedTheme}.svg`)
            }
            style={{ objectFit: 'contain' }}
          />

          <div className="flex flex-col">
            <p className="-mb-1 line-clamp-1 text-sm text-text-gray" title={tenant?.name}>
              {tenant?.name}
            </p>
            <p className="line-clamp-1 text-base leading-5 font-semibold uppercase">
              {isPublicRoute || isPublicSharePage ? node?.name : 'DAM'}
            </p>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'flex h-full items-center justify-between gap-x-2 bg-ui-base',
          !isPublicRoute && !isPublicSharePage && 'w-full',
        )}
      >
        <>
          {!isPublicRoute && (canUndoRedo || canSearch) && (
            <>
              {canUndoRedo && (
                <div className="flex">
                  <Button
                    variant="main-toolbar"
                    size="main-toolbar"
                    className="p-4"
                    onClick={undo}
                    disabled={history.length === 0 || pointer - 1 < 0}
                    tooltip={`Undo: ${history[pointer - 1]?.message ?? ''}`}
                    data-cy="navigation-undo-button"
                    icon={Undo2}
                    iconClassName="stroke-icon-base"
                  />
                  <Button
                    variant="main-toolbar"
                    size="main-toolbar"
                    className="p-4"
                    onClick={redo}
                    disabled={history.length === 0 || pointer + 1 > history.length}
                    tooltip={`Redo: ${history[pointer]?.message ?? ''}`}
                    data-cy="navigation-redo-button"
                    icon={Redo2}
                    iconClassName="stroke-icon-base"
                  />
                </div>
              )}

              {canSearch && !singleAssetView && !singleAssetShare && (
                <div className="w-full justify-start">
                  <QuickSearch />
                </div>
              )}
            </>
          )}
          <div className="ml-auto flex items-center gap-x-3 px-3">
            {!singleAssetView && !isPublicRoute && canUpload && (
              <Button
                onClick={() => {
                  const { uploadSessionId } = getActiveUploadSession();

                  if (selectedNode?.node?.id) {
                    if (selectedNode.type === DAMNodeType.Folder) {
                      // Set upload folder to selected folder and clear any album preselection
                      const path = findNodePath(selectedNode.node.id, 'name')?.join('/');
                      if (path) {
                        setUploadFolderPath(uploadSessionId, `/${path}`);
                        setReusableTreeSelectedFolder(selectedNode.node.id);
                      } else {
                        setUploadFolderPath(uploadSessionId, '');
                        setReusableTreeSelectedFolder(undefined);
                        setReusableTreeSelection(undefined);
                      }

                      // Clear any previous album preselection
                      useUploadStore.getState().clearSelectedAlbums(uploadSessionId);
                      setUploadModalOpen(true);
                      return;
                    }

                    if (selectedNode.type === DAMNodeType.Album) {
                      // Compute parent folder path and id
                      const namePath = (findNodePath(selectedNode.node.id, 'name') ?? []) as Array<string>;
                      const idPath = (findNodePath(selectedNode.node.id, 'id') ?? []) as Array<string>;

                      const parentNodePath = namePath.length > 1 ? `/${namePath.slice(0, -1).join('/')}` : '';
                      const parentId = idPath.length > 1 ? idPath[idPath.length - 2] : undefined;

                      // Preselect parent folder
                      setUploadFolderPath(uploadSessionId, parentNodePath);
                      if (parentId) {
                        setReusableTreeSelectedFolder(parentId);
                      } else {
                        setReusableTreeSelectedFolder(undefined);
                        setReusableTreeSelection(undefined);
                      }

                      // Preselect selected album in the upload form
                      useUploadStore
                        .getState()
                        .setSelectedAlbums(uploadSessionId, [
                          { id: selectedNode.node.id, label: selectedNode.node.name },
                        ]);

                      setUploadModalOpen(true);
                      return;
                    }
                  }

                  // Fallback (no selection or unsupported type)
                  setUploadFolderPath(uploadSessionId, '');
                  setReusableTreeSelectedFolder(undefined);
                  setReusableTreeSelection(undefined);
                  useUploadStore.getState().clearSelectedAlbums(uploadSessionId);
                  setUploadModalOpen(true);
                }}
                variant="navigation-action-primary"
                className="focus-visible:ring-offset-ui-base"
                size="navigation-action"
                data-cy="navigation-uploads-button"
                icon={Upload}
                iconClassName="icon-5"
              >
                {tUpload('upload')}
              </Button>
            )}
            {!singleAssetView && canViewNotifications && <NotificationDropdown />}
            {singleAssetView && !singleAssetShare && (
              <>
                {/* <Button
                  variant="navigation-action-secondary"
                  size="navigation-action"
                  data-cy="navigation-edit-button"
                  className="focus-visible:ring-offset-ui-base"
                  icon={Crop}
                  iconClassName="icon-5"
                >
                  Edit
                </Button>
                <Button
                  variant="navigation-action-primary"
                  className="focus-visible:ring-offset-ui-base"
                  size="navigation-action"
                  data-cy="navigation-share-button"
                  icon={LinkIcon}
                  iconClassName="icon-5"
                >
                  Share
                </Button> */}

                <Button
                  onClick={handleBack}
                  variant="main-toolbar"
                  size="main-toolbar"
                  className="hover:bg-[unset]! hover:text-text-base"
                  data-cy="navigation-back-button"
                  icon={X}
                  iconClassName="icon-5 stroke-text-gray!"
                  tooltip={tCommon('close')}
                />
              </>
            )}
          </div>
        </>
      </div>
    </div>
  );
};
